export type Environment = 'production' | 'staging' | 'development' | 'test'

/**
 * Get the current environment.
 *
 * Environment detection is based on Vite's built-in MODE and PROD variables,
 * which are set at build time via the --mode flag.
 *
 * @see https://v6.vite.dev/guide/env-and-mode.html
 */
export function getEnvironment(): Environment {
  const mode = import.meta.env.MODE as Environment | undefined
  const isProductionBuild = import.meta.env.PROD // Set by Vite, equals to NODE_ENV === 'production'

  if (!mode) {
    throw new Error('Vite mode is not set')
  }

  // 1. Detect production environment based on Vite env variables
  if (mode === 'production' && isProductionBuild) {
    return 'production'
  }

  // 2. Detect environment based on Vite Mode
  if (['staging', 'development'].includes(mode)) {
    return mode
  }

  // 3. Detect test environment
  if (process.env.NODE_ENV === 'test') {
    return 'test'
  }

  // 4. Default to development environment
  return 'development'
}

export const environment = getEnvironment()
export const isProduction = environment === 'production'
export const isStaging = environment === 'staging'
export const isDevelop = environment === 'development'
export const isDevServer = process.env.NODE_ENV === 'development'
export const isTesting = environment === 'test'

const searchParams = new URLSearchParams(window.location.search)
const debugParam = searchParams.get('debug') === 'true'
const debugLS = localStorage.getItem('debug') === 'true'

// Only show the devtools if
// - we're not in production, OR
// - we explicitly opted in via `?debug=true` OR
// - we set a local storage variable with `localStorage.setItem('debug', 'true')`
export const debugModeAvailable = !isProduction || debugParam || debugLS
